import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { IRetentionDetails } from "../../core/api/organizations/types";
import useTranslations from "../../core/i18n/useTranslations";
import {
  useObjectForm,
  useObjectFormRef,
} from "../../core/SaveObjectContext/hooks";
import { IDropdownItem } from "../../ui-lib/components/Dropdown/DropdownItem";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";
import { Spacer, TwoColGrid } from "../Layout/Layout";
import Divider from "../../ui-lib/components/Divider/Divider";
import { RetentionConfirmModal } from "./RetentionConfirmModal";

export const RetentionForm = ({
  defaultValues,
  retentionOptions,
  onSubmit,
}: {
  defaultValues?: IRetentionDetails;
  retentionOptions: IDropdownItem[];
  onSubmit: (values: IRetentionDetails) => Promise<void>;
}) => {
  const t = useTranslations();
  const formRef = useObjectFormRef();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
  } = useObjectForm<IRetentionDetails>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const validateAlarmDataRetention = (
    data: number | undefined,
    watcher: number,
    message: string,
  ) => {
    if (data === undefined) return message;
    if (data === 0 || data === -1)
      return t("Organizations:not_valid_for_field");
    if (data !== 0 && watcher === 0) return message;
    if (data >= watcher) return true;
    return message;
  };

  const validateObjectDataRetention = (
    data: number | undefined,
    watcher: number,
    message: string,
  ) => {
    if (data === undefined) return message;
    if (data === 0) return true;
    if (data !== 0 && watcher === 0) return message;
    if (data >= watcher) return true;
    return message;
  };

  const validateMediaRetention = (
    data: number | undefined,
    watcher: number,
    message: string,
  ) => {
    if (data === undefined) return message;
    if (watcher === 0) return true;
    if (data === 0 && watcher !== 0) return message;
    if (data <= watcher) return true;
    return message;
  };

  const retentionValidate = (
    data: number | undefined,
    watcher: number,
    message: string,
    fieldName: string,
  ) => {
    switch (fieldName) {
      case "alarmDataRetention":
        return validateAlarmDataRetention(data, watcher, message);
      case "objectDataRetention":
        return validateObjectDataRetention(data, watcher, message);
      case "alarmMediaRetention":
      case "objectMediaRetention":
      case "logArchiveRetention":
        return validateMediaRetention(data, watcher, message);
      default:
        return message;
    }
  };

  const optionsToSelect = (currentSelectedId: number) => {
    let retentionOptionsToUse =
      currentSelectedId !== 0
        ? [...retentionOptions].slice(1)
        : retentionOptions;
    retentionOptionsToUse =
      currentSelectedId !== -1
        ? retentionOptionsToUse.slice(0, retentionOptionsToUse.length - 1)
        : retentionOptionsToUse;
    return retentionOptionsToUse.map((i) => ({
      ...i,
      isSelected: +i.id === currentSelectedId,
    }));
  };

  return (
    <>
      <form ref={formRef} onSubmit={() => setIsConfirmModalOpen(true)}>
        <h2>{t("Organizations:alarm_data_retention")}</h2>
        <p>{t("Organizations:retention_time_for_alarm")}</p>
        <Spacer size={16} />
        <TwoColGrid>
          <Controller
            name="alarmDataRetention"
            control={control}
            rules={{
              validate: {
                organisationData: (value: number) =>
                  retentionValidate(
                    value,
                    watch("alarmMediaRetention"),
                    `${t("Organizations:should_be_more_or_equal")} ${t(
                      "Organizations:media_retention",
                    )}`,
                    "alarmDataRetention",
                  ),
              },
            }}
            render={({ field }) => {
              const alarmRetentionData = optionsToSelect(field.value);
              return (
                <Dropdown
                  className="dropdownItem"
                  selectedItem={alarmRetentionData.find((i) => i.isSelected)}
                  title={t("Organizations:alarm_data_retention")}
                  onSelectItem={(item) => {
                    setValue(field.name, +item.id);
                    trigger(["alarmMediaRetention", "alarmDataRetention"]);
                  }}
                  items={alarmRetentionData}
                  validationError={errors?.alarmDataRetention?.message}
                />
              );
            }}
          />
          <Controller
            name="alarmMediaRetention"
            control={control}
            rules={{
              validate: {
                organisationMedia: (value) =>
                  retentionValidate(
                    value,
                    watch("alarmDataRetention"),
                    `${t("Organizations:should_be_less_or_equal")} ${t(
                      "Organizations:alarm_data_retention",
                    )}`,
                    "alarmMediaRetention",
                  ),
              },
            }}
            render={({ field }) => {
              const alarmMediaRetention = optionsToSelect(field.value);
              return (
                <Dropdown
                  className="dropdownItem"
                  title={t("Organizations:media_retention")}
                  selectedItem={alarmMediaRetention.find((i) => i.isSelected)}
                  onSelectItem={(item) => {
                    setValue(field.name, +item.id);
                    trigger(["alarmMediaRetention", "alarmDataRetention"]);
                  }}
                  items={alarmMediaRetention}
                  validationError={errors?.alarmMediaRetention?.message}
                />
              );
            }}
          />
        </TwoColGrid>

        <Spacer size={16} />
        <Divider />
        <Spacer size={16} />

        <h2>{t("Organizations:object_retention")}</h2>
        <p>{t("Organizations:months_after_subscription_cancelled")}</p>
        <Spacer size={16} />
        <TwoColGrid style={{ rowGap: "16px" }}>
          <Controller
            name="alarmAfterObjectDeleteRetention"
            control={control}
            render={({ field }) => {
              const alarmAfterObjectDeleteRetention = optionsToSelect(
                field.value,
              );
              return (
                <Dropdown
                  className="dropdownItem"
                  title={t("Organizations:static_data")}
                  selectedItem={alarmAfterObjectDeleteRetention.find(
                    (i) => i.isSelected,
                  )}
                  onSelectItem={(item) => {
                    setValue(field.name, +item.id);
                  }}
                  items={alarmAfterObjectDeleteRetention}
                />
              );
            }}
          />
          <Controller
            name="objectDataRetention"
            control={control}
            rules={{
              validate: {
                objectData: (value) =>
                  retentionValidate(
                    value,
                    watch("objectMediaRetention"),
                    `${t("Organizations:should_be_more_or_equal")} ${t(
                      "Organizations:media_data_associated_with_object",
                    )}`,
                    "objectDataRetention",
                  ),
              },
            }}
            render={({ field }) => {
              const objectDataRetention = optionsToSelect(field.value);
              return (
                <Dropdown
                  className="dropdownItem"
                  title={t("Organizations:alarm_data_associated_with_object")}
                  selectedItem={objectDataRetention.find((i) => i.isSelected)}
                  onSelectItem={(item) => {
                    setValue(field.name, +item.id);
                    trigger(["objectDataRetention", "objectMediaRetention"]);
                  }}
                  items={objectDataRetention}
                  validationError={errors?.objectDataRetention?.message}
                />
              );
            }}
          />
          <Controller
            name="objectMediaRetention"
            control={control}
            rules={{
              validate: {
                objectMedia: (value) =>
                  retentionValidate(
                    value,
                    watch("objectDataRetention"),
                    `${t("Organizations:should_be_less_or_equal")} ${t(
                      "Organizations:alarm_data_associated_with_object",
                    )}`,
                    "objectMediaRetention",
                  ),
              },
            }}
            render={({ field }) => {
              const objectMediaRetention = optionsToSelect(field.value);
              return (
                <Dropdown
                  className="dropdownItem"
                  title={t("Organizations:media_data_associated_with_object")}
                  selectedItem={objectMediaRetention.find((i) => i.isSelected)}
                  onSelectItem={(item) => {
                    setValue(field.name, +item.id);
                    trigger(["objectDataRetention", "objectMediaRetention"]);
                  }}
                  items={objectMediaRetention}
                  validationError={errors?.objectMediaRetention?.message}
                />
              );
            }}
          />
        </TwoColGrid>
      </form>
      <RetentionConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onSubmit={handleSubmit(async (values) => {
          await onSubmit(values);
          setIsConfirmModalOpen(false);
          reset();
        })}
      />
    </>
  );
};
